/**
 * Acceptable Use Policy Page
 * 
 * Legal document for NewAudia Systems, LLC
 * Version 1.1 | Effective Date: March 1, 2026
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Mail } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { useAuth } from '../contexts/AuthContext';
import { Seo, PAGE_SEO } from '../components/Seo';

const AcceptableUsePage = () => {
  const { user } = useAuth();
  
  const getHomeRoute = () => {
    if (!user) return '/';
    const roles = user.roles || [];
    if (roles.includes('admin')) return '/admin';
    if (roles.includes('publisher')) return '/publisher';
    if (roles.includes('author') || roles.includes('creator')) return '/creator';
    if (roles.includes('reader')) return '/home';
    return '/';
  };
  
  const homeRoute = getHomeRoute();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <Seo {...PAGE_SEO.acceptableUse} />
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={homeRoute} className="flex items-center gap-3">
            <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-10 w-auto rounded-lg" />
          </Link>
          <Link 
            to={homeRoute}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Document Header */}
        <div className="mb-12 pb-8 border-b border-slate-700">
          <div className="flex items-center gap-4 mb-6">
            <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-16 w-auto rounded-lg" />
            <div>
              <h1 data-testid="aup-company-name" className="text-3xl font-bold text-white">NewAudia Systems, LLC</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="w-6 h-6 text-indigo-400" />
            <h2 data-testid="aup-title" className="text-2xl font-semibold text-white">Acceptable Use Policy</h2>
          </div>
          
          <div className="flex flex-wrap gap-6 text-sm text-slate-400">
            <p><span className="text-slate-500">Effective Date:</span> March 1, 2026</p>
            <p><span className="text-slate-500">Version:</span> 1.1</p>
            <p><span className="text-slate-500">Last Updated:</span> March 2026</p>
          </div>
        </div>

        {/* Policy Content */}
        <div className="prose prose-invert prose-slate max-w-none">
          {/* Important Notice */}
          <section className="mb-10">
            <p className="text-amber-400 font-semibold text-sm uppercase tracking-wide mb-4">Important — Please Read Carefully</p>
            <p className="text-slate-300 leading-relaxed">
              This Acceptable Use Policy ("AUP") governs access to and use of the NewAudia platform, website, 
              applications, and services (collectively, the "Services"). This AUP applies to ALL users, including 
              Readers, Authors, Publishers, and Administrators. By accessing or using the Services, you agree to 
              comply with this AUP in its entirety.
            </p>
          </section>

          {/* Company Identification */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Company Identification</h3>
            <p className="text-slate-300 leading-relaxed">
              The Services are operated by <strong className="text-white">NewAudia Systems, LLC</strong>, a limited liability 
              company organized under the laws of the State of Colorado, with its principal place of business in Colorado Springs, 
              Colorado (hereinafter "NewAudia," "Company," "we," "us," or "our"). "NewAudia" is a registered trade name of 
              NewAudia Systems, LLC in the State of Colorado.
            </p>
            <p className="text-slate-300 leading-relaxed mt-4">
              This AUP is incorporated by reference into the NewAudia Terms of Service and, for Authors and Publishers, 
              into the NewAudia Author Publishing Agreement. In the event of a conflict between this AUP and the Author 
              Publishing Agreement with respect to Author obligations, the Author Publishing Agreement controls.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">1. Purpose and Scope</h3>
            <p className="text-slate-300 leading-relaxed mb-3">The purpose of this AUP is to:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Protect users, content owners, and the platform from abuse and harm</li>
              <li>Ensure lawful, ethical, and fair use of the Services by all user types</li>
              <li>Define the prohibited conduct and content standards that all users must observe</li>
              <li>Establish NewAudia's enforcement rights and remedies</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              This policy applies to all interactions with the Services, whether automated or manual, and to all user 
              roles including Readers, Authors, Publishers, and Administrators.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">2. Permitted Use</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              You may use the Services solely for lawful purposes and in accordance with:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>This Acceptable Use Policy</li>
              <li>The NewAudia Terms of Service</li>
              <li>The NewAudia Author Publishing Agreement (Authors and Publishers only)</li>
              <li>All applicable local, state, federal, and international laws and regulations</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">3. Prohibited Conduct</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              You may not engage in any activity that NewAudia determines, in its sole discretion, to be abusive, 
              harmful, deceptive, or unlawful. The categories below are illustrative and not exhaustive.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">3.1 Legal and Rights Violations</h4>
            <p className="text-slate-300 leading-relaxed mb-3">You may not:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Violate any applicable law or regulation</li>
              <li>Infringe any intellectual property right, including copyright, trademark, or trade secret</li>
              <li>Violate any privacy right, right of publicity, or contractual right of any third party</li>
              <li>Distribute, reproduce, or publish content without proper authorization from all applicable rights holders</li>
            </ul>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">3.2 Platform Abuse and Security Violations</h4>
            <p className="text-slate-300 leading-relaxed mb-3">You may not:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Circumvent, disable, or interfere with access controls, rate limits, or billing and payment systems</li>
              <li>Scrape, crawl, index, or harvest data from the Services without NewAudia's prior written authorization</li>
              <li>Reverse engineer, decompile, disassemble, or otherwise attempt to extract source code or underlying systems</li>
              <li>Attempt to gain unauthorized access to any account, system, database, or infrastructure component of the Services</li>
              <li>Use automated scripts, bots, or other mechanisms to interact with the Services in a manner inconsistent with normal human use</li>
            </ul>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">3.3 Harmful or Disruptive Behavior</h4>
            <p className="text-slate-300 leading-relaxed mb-3">You may not:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Upload, transmit, or introduce malware, viruses, exploits, ransomware, or any other harmful or malicious code</li>
              <li>Interfere with or degrade the performance, stability, or availability of the Services or any connected infrastructure</li>
              <li>Harass, threaten, intimidate, or abuse other users, Authors, Publishers, or NewAudia staff</li>
              <li>Impersonate NewAudia, its employees, or any other person or entity</li>
            </ul>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">3.4 Prohibited Content — All Users</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              You may not upload, submit, post, transmit, or otherwise make available through the Services any content that:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Is defamatory, fraudulent, deceptive, or materially misleading</li>
              <li>Infringes any copyright, trademark, trade secret, right of publicity, or other intellectual property right</li>
              <li>Constitutes, promotes, or facilitates illegal activity, including hate speech, incitement to violence, or discrimination based on race, religion, gender, sexual orientation, national origin, or disability</li>
              <li>Is sexually explicit, pornographic, or obscene</li>
              <li>Depicts, promotes, or facilitates harm to minors in any form</li>
              <li>Invades the privacy of any identifiable living person without their consent</li>
              <li>Consists of spam, artificially generated filler text, or content designed solely to manipulate Platform metrics, reader engagement statistics, or Commission calculations</li>
              <li>Contains malware, harmful code, or any mechanism intended to damage, interfere with, or gain unauthorized access to the Services or any user's systems</li>
              <li>The submitting user does not have the full legal right to publish and distribute</li>
            </ul>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">3.5 Prohibited Conduct — Authors and Publishers</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              In addition to the restrictions above, Authors and Publishers may not:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Misrepresent authorship, ownership, or the licensing status of any Manuscript</li>
              <li>Submit a Manuscript that is currently the subject of a copyright infringement claim or legal proceeding without prior written disclosure to NewAudia</li>
              <li>Engage in any conduct intended to game, manipulate, or artificially inflate Commission earnings, Reader Credit consumption statistics, or engagement metrics</li>
              <li>Submit substantially identical or duplicate Manuscripts under different titles for the purpose of circumventing ingestion fees or Platform policies</li>
              <li>Use the Publisher Portal to manage authors whose Manuscripts have been previously removed or banned from the Services</li>
            </ul>
          </section>

          {/* Section 4 - Upload Fees */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">4. Manuscript Upload Fees</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              The following fees apply to Manuscript ingestion through the NewAudia platform. These fees support 
              platform infrastructure, AI narration processing, and cloud storage operations.
            </p>
            <div className="overflow-x-auto mb-6">
              <table data-testid="aup-fee-table" className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Upload Sequence</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Fee</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Notes</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-slate-300">First Successful Ingestion</td>
                    <td className="py-3 px-4 text-emerald-400 font-medium">FREE</td>
                    <td className="py-3 px-4 text-slate-400">Per Author account; waived automatically at upload.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-slate-300">Each Subsequent Successful Ingestion</td>
                    <td className="py-3 px-4 text-white font-medium">$9.99 USD</td>
                    <td className="py-3 px-4 text-slate-400">Charged per successful pipeline completion via Stripe.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-slate-300">Failed or Non-Compliant Ingestion</td>
                    <td className="py-3 px-4 text-red-400 font-medium">Non-refundable</td>
                    <td className="py-3 px-4 text-slate-400">Fees are forfeited on policy violation, ban, or account misuse. See Section 5.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-slate-300 leading-relaxed text-sm">
              All ingestion fees are non-refundable once a Manuscript has been submitted for processing, regardless of outcome. 
              Fees paid in connection with any Manuscript found to violate this AUP, the Author Publishing Agreement, or 
              Community Standards — or associated with a suspended or permanently banned account — are expressly and irrevocably 
              non-refundable.
            </p>
          </section>

          {/* Section 5 - Enforcement */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">5. Enforcement and Remedies</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              NewAudia reserves the right, in its sole and unreviewable discretion, to take any of the following 
              actions in response to actual or suspected violations of this AUP, the Terms of Service, or the 
              Author Publishing Agreement:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Investigate suspected violations using platform logs, content analysis, and any other available methods</li>
              <li>Remove, restrict, or withhold from public access any content, Manuscript, or Audiobook</li>
              <li>Suspend or restrict access to an account, temporarily or permanently</li>
              <li>Permanently ban an Author, Publisher, or Reader from the Services, including all associated accounts</li>
              <li>Forfeit any unpaid Commissions or earned credits associated with non-compliant Manuscripts or banned accounts</li>
              <li>Report conduct to law enforcement or regulatory authorities, and cooperate fully with any resulting investigation or proceeding</li>
              <li>Pursue legal remedies available under applicable law</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              Enforcement actions may be taken without prior notice. NewAudia is not obligated to provide a reason 
              for any enforcement action. NewAudia's exercise of any enforcement right shall not be deemed a breach 
              of any agreement with the affected user.
            </p>

            {/* Permanent Ban Policy */}
            <div className="bg-red-950/30 border border-red-900/50 rounded-lg p-6 mt-6">
              <h4 className="text-lg font-semibold text-red-400 mb-3">Permanent Ban Policy</h4>
              <p className="text-slate-300 leading-relaxed text-sm">
                In cases of egregious, repeated, or malicious violations of this AUP, the Terms of Service, or the 
                Author Publishing Agreement, NewAudia may permanently ban a user's account and all associated or related 
                accounts. A permanent ban results in: (a) immediate removal of all associated content from public access; 
                (b) forfeiture of any unpaid Commissions or credits accrued in connection with non-compliant content; 
                (c) non-refundability of all ingestion fees paid; and (d) prohibition on creating new accounts on the 
                Platform, including under different names, email addresses, or entities. NewAudia's determination is 
                made at its sole discretion and is not subject to internal appeal.
              </p>
            </div>

            {/* Content Removal Clause */}
            <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-6 mt-4">
              <h4 className="text-lg font-semibold text-slate-200 mb-3">Sole Discretion — Content Removal</h4>
              <p className="text-slate-300 leading-relaxed text-sm">
                NewAudia reserves the absolute right to remove any content, Manuscript, or Audiobook from public 
                access at any time, for any reason or no stated reason, at its sole and unreviewable discretion. This 
                right is not limited to violations of this AUP or any other policy document. NewAudia's exercise of 
                this right shall not be deemed a breach of any agreement.
              </p>
            </div>
          </section>

          {/* Section 6 - Reporting */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">6. Reporting Violations</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you believe a user, Author, Publisher, or piece of content on the Services is in violation of this 
              AUP, the Terms of Service, or applicable law, please report it to NewAudia promptly using the contact 
              information below. Reports of copyright infringement should include sufficient detail to allow NewAudia 
              to identify the content in question.
            </p>
            <div data-testid="aup-contact-info" className="bg-slate-900/50 border border-slate-700 rounded-lg p-6">
              <p className="text-white font-semibold text-lg">NewAudia Systems, LLC</p>
              <p className="text-slate-400 text-sm">A Colorado Limited Liability Company</p>
              <p className="text-slate-400 text-sm">"NewAudia" is a registered trade name in Colorado</p>
              <p className="text-slate-400 mt-2">Colorado Springs, Colorado, USA</p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-400 text-sm">General Inquiries & Policy Reports:</span>
                  <a href="mailto:info@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors text-sm">
                    info@newaudia.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-400 text-sm">Administrative & Legal Notices:</span>
                  <a href="mailto:admin@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors text-sm">
                    admin@newaudia.com
                  </a>
                </div>
              </div>
              <p className="text-slate-400 text-sm mt-3">
                Website: <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>
              </p>
            </div>
          </section>

          {/* Section 7 - Disclaimer */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">7. Disclaimer of Warranties</h3>
            <p className="text-slate-300 leading-relaxed text-sm uppercase tracking-wide">
              The Services are provided "as is" and "as available" without warranty of any kind, whether express, implied, 
              or statutory. NewAudia disclaims all warranties, including but not limited to implied warranties of merchantability, 
              fitness for a particular purpose, and non-infringement. NewAudia does not warrant that the Services will be 
              uninterrupted, error-free, or free of harmful components.
            </p>
          </section>

          {/* Section 8 - Liability */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">8. Limitation of Liability</h3>
            <p className="text-slate-300 leading-relaxed text-sm uppercase tracking-wide">
              To the maximum extent permitted by applicable law, NewAudia's total cumulative liability to any user for any 
              claim arising under or related to this AUP shall not exceed fifty dollars ($50.00 USD). NewAudia shall not be 
              liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, including lost 
              profits or lost data, even if advised of the possibility of such damages.
            </p>
          </section>

          {/* Section 9 - Policy Updates */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">9. Policy Updates</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              NewAudia may update this AUP at any time by posting a revised version on the Services. NewAudia will use 
              reasonable efforts to notify users of material changes via email to the address on file. Continued use of 
              the Services following notice of any update constitutes acceptance of the revised AUP.
            </p>

            {/* Version History Table */}
            <h4 className="text-lg font-medium text-white mb-3">Version History</h4>
            <div className="overflow-x-auto">
              <table data-testid="aup-version-history" className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-20">Version</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-36">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Summary of Changes</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-slate-300">1.0</td>
                    <td className="py-3 px-4 text-slate-400">January 1, 2026</td>
                    <td className="py-3 px-4 text-slate-400">Initial publication. Entity listed as NewAudia dba Use What You Got Publishing, LLC.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">1.1</td>
                    <td className="py-3 px-4 text-slate-300">March 2026</td>
                    <td className="py-3 px-4 text-slate-400">
                      Entity updated to NewAudia Systems, LLC (registered trade name in Colorado). Added permanent ban policy, 
                      sole discretion content removal clause, Author/Publisher-specific prohibited conduct, upload fee table 
                      with non-refundability language, disclaimer of warranties, limitation of liability, and alignment with 
                      Author Publishing Agreement v1.0.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Footer Entity Statement */}
          <section className="mt-12 pt-8 border-t border-slate-700">
            <p className="text-sm text-slate-500">
              NewAudia Systems, LLC | Colorado Springs, Colorado |{' '}
              <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>
              {' '}| <a href="mailto:admin@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">admin@newaudia.com</a>
            </p>
            <p className="text-sm text-slate-500 mt-2 italic">
              "NewAudia" is a registered trade name of NewAudia Systems, LLC in the State of Colorado.
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Version 1.1 — March 2026
            </p>
          </section>
        </div>
      </main>

      <UniversalFooter />
    </div>
  );
};

export default AcceptableUsePage;
